import React, { useRef, useMemo, useEffect } from 'react';
import JoditEditor from 'jodit-react';

function RichTextEditor({ value, onChange, label, className = '' }) {
  const editor = useRef(null);

  const config = useMemo(() => ({
    readonly: false,
    placeholder: 'Enter detailed description here... (e.g., package content, FAQ answer)',
    height: 250,
    toolbarButtonSize: 'small',
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    statusbar: false,
    theme: 'default',
    buttons: [
      'bold', 'italic', 'underline', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', '|',
      'align', '|',
      'link', 'image', '|',
      'table', '|',
      'undo', 'redo', '|',
      'hr', '|',
      'fullsize', 'source'
    ],
    removeButtons: ['about'],
    disablePlugins: ['about'],
    uploader: {
      insertImageAsBase64URI: true
    },
    style: {
      font: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px',
      lineHeight: '1.5',
      color: '#111827'
    },
    enter: 'P',
    cleanHTML: {
      removeEmptyElements: false,
      replaceNBSP: false,
    },
    // Enable context menu for list options
    usePopupForSpecialCharacters: true,
  }), []);

  useEffect(() => {
    if (editor.current) {
      const joditInstance = editor.current;
      
      // Add event listener for when lists are created
      joditInstance.events.on('afterCommand', (command) => {
        if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
          // Small delay to ensure DOM is updated
          setTimeout(() => {
            const selection = joditInstance.selection.sel;
            if (selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              let currentElement = range.commonAncestorContainer;
              
              // Navigate up to find the list element
              while (currentElement && currentElement.nodeType !== 1) {
                currentElement = currentElement.parentNode;
              }
              
              while (currentElement && !['UL', 'OL'].includes(currentElement.tagName)) {
                currentElement = currentElement.parentNode;
                if (currentElement === joditInstance.editor) break;
              }
              
              if (currentElement && ['UL', 'OL'].includes(currentElement.tagName)) {
                // Add context menu for list styling
                addListContextMenu(joditInstance, currentElement);
              }
            }
          }, 100);
        }
      });
    }
  }, []);

  const addListContextMenu = (joditInstance, listElement) => {
    // Remove any existing context menu listeners
    listElement.removeEventListener('contextmenu', handleListContextMenu);
    
    // Add right-click context menu
    listElement.addEventListener('contextmenu', (e) => handleListContextMenu(e, joditInstance, listElement));
  };

  const handleListContextMenu = (e, joditInstance, listElement) => {
    e.preventDefault();
    
    // Remove any existing popup
    const existingPopup = document.querySelector('.list-style-popup');
    if (existingPopup) {
      existingPopup.remove();
    }
    
    const popup = document.createElement('div');
    popup.className = 'list-style-popup';
    popup.style.cssText = `
      position: fixed;
      top: ${e.clientY}px;
      left: ${e.clientX}px;
      background: white;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      z-index: 10000;
      padding: 8px;
      min-width: 150px;
    `;
    
    const isOrderedList = listElement.tagName === 'OL';
    const options = isOrderedList 
      ? [
          { type: 'decimal', label: '1. Numbers' },
          { type: 'decimal-leading-zero', label: '01. Leading Zero' },
          { type: 'lower-alpha', label: 'a. Lower Alpha' },
          { type: 'upper-alpha', label: 'A. Upper Alpha' },
          { type: 'lower-roman', label: 'i. Lower Roman' },
          { type: 'upper-roman', label: 'I. Upper Roman' },
        ]
      : [
          { type: 'disc', label: '● Disc' },
          { type: 'circle', label: '○ Circle' },
          { type: 'square', label: '■ Square' },
        ];
    
    options.forEach(option => {
      const button = document.createElement('div');
      button.innerHTML = option.label;
      button.style.cssText = `
        padding: 6px 12px;
        cursor: pointer;
        border-radius: 2px;
        transition: background-color 0.2s;
      `;
      
      button.addEventListener('mouseenter', () => {
        button.style.backgroundColor = '#f0f0f0';
      });
      
      button.addEventListener('mouseleave', () => {
        button.style.backgroundColor = 'transparent';
      });
      
      button.addEventListener('click', () => {
        listElement.style.listStyleType = option.type;
        popup.remove();
        
        // Trigger change event
        if (onChange) {
          onChange(joditInstance.getEditorValue());
        }
      });
      
      popup.appendChild(button);
    });
    
    document.body.appendChild(popup);
    
    // Close popup when clicking outside
    setTimeout(() => {
      document.addEventListener('click', function closePopup() {
        popup.remove();
        document.removeEventListener('click', closePopup);
      });
    }, 100);
  };

  const handleChange = (content) => {
    if (onChange) {
      onChange(content);
    }
  };

  return (
    <div className={`mb-6 ${className}`}>
      {label && (
        <label className="block text-gray-700 text-sm font-semibold mb-3">
          {label}
        </label>
      )}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-1 rounded-xl border border-indigo-200 shadow-sm">
        <div className="jodit-editor-wrapper bg-white rounded-lg overflow-hidden shadow-sm">
          <JoditEditor
            ref={editor}
            value={value || ''}
            config={config}
            tabIndex={1}
            onBlur={handleChange}
            onChange={() => {}} // Controlled by onBlur to avoid performance issues
          />
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        💡 Tip: Right-click on any list to change its style (bullets/numbers)
      </p>
      <style jsx global>{`
        .jodit-editor-wrapper .jodit-container {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          font-family: system-ui, -apple-system, sans-serif;
        }
        .jodit-editor-wrapper .jodit-toolbar {
          background: linear-gradient(to right, #f8fafc, #f1f5f9);
          border-bottom: 1px solid #e2e8f0;
          border-radius: 8px 8px 0 0;
        }
        .jodit-editor-wrapper .jodit-workplace {
          background-color: #ffffff;
        }
        .jodit-editor-wrapper .jodit-wysiwyg {
          background-color: #ffffff;
          font-family: system-ui, -apple-system, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: #111827;
          padding: 12px;
        }
        .jodit-editor-wrapper .jodit-container:focus-within {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .jodit-editor-wrapper .jodit-toolbar-button {
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        .jodit-editor-wrapper .jodit-toolbar-button:hover {
          background-color: rgba(99, 102, 241, 0.1) !important;
          border-color: transparent !important;
        }
        .jodit-editor-wrapper .jodit-toolbar-button_active {
          background-color: rgba(99, 102, 241, 0.2) !important;
        }
        .jodit-editor-wrapper .jodit-status-bar {
          display: none;
        }

        /* Enhanced list styling */
        .jodit-editor-wrapper .jodit-wysiwyg ul,
        .jodit-editor-wrapper .jodit-wysiwyg ol {
          margin: 10px 0 !important;
          padding-left: 30px !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg li {
          display: list-item !important;
          margin: 4px 0 !important;
          padding-left: 0 !important;
        }

        /* Default list styles */
        .jodit-editor-wrapper .jodit-wysiwyg ul {
          list-style-type: disc !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg ol {
          list-style-type: decimal !important;
        }

        /* All supported list style types */
        .jodit-editor-wrapper .jodit-wysiwyg ul[style*="list-style-type: disc"] {
          list-style-type: disc !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg ul[style*="list-style-type: circle"] {
          list-style-type: circle !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg ul[style*="list-style-type: square"] {
          list-style-type: square !important;
        }

        .jodit-editor-wrapper .jodit-wysiwyg ol[style*="list-style-type: decimal"] {
          list-style-type: decimal !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg ol[style*="list-style-type: decimal-leading-zero"] {
          list-style-type: decimal-leading-zero !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg ol[style*="list-style-type: lower-alpha"] {
          list-style-type: lower-alpha !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg ol[style*="list-style-type: upper-alpha"] {
          list-style-type: upper-alpha !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg ol[style*="list-style-type: lower-roman"] {
          list-style-type: lower-roman !important;
        }
        
        .jodit-editor-wrapper .jodit-wysiwyg ol[style*="list-style-type: upper-roman"] {
          list-style-type: upper-roman !important;
        }

        /* Nested list handling */
        .jodit-editor-wrapper .jodit-wysiwyg ul ul,
        .jodit-editor-wrapper .jodit-wysiwyg ol ol {
          margin: 5px 0 !important;
        }

        /* Fix for list item paragraphs */
        .jodit-editor-wrapper .jodit-wysiwyg li p {
          margin: 0 !important;
          display: inline !important;
        }
        
        /* Fix for nested lists */
        .jodit-editor-wrapper .jodit-wysiwyg li > ul,
        .jodit-editor-wrapper .jodit-wysiwyg li > ol {
          margin-top: 5px !important;
          margin-bottom: 5px !important;
        }

        /* Ensure proper list display */
        .jodit-editor-wrapper .jodit-wysiwyg ul li,
        .jodit-editor-wrapper .jodit-wysiwyg ol li {
          display: list-item !important;
          list-style-position: outside !important;
        }

        /* Style for the context menu popup */
        .list-style-popup {
          font-family: system-ui, -apple-system, sans-serif;
          font-size: 14px;
        }
      `}</style>
    </div>
  );
}

export default RichTextEditor;